import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross1Icon,
  MagicWandIcon,
  PaperPlaneIcon,
} from '@radix-ui/react-icons';
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
  TextArea,
} from '@radix-ui/themes';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';

import {
  activeChatMessagesAtom,
  activeEditorAtom,
  addChatMessagesAction,
  aiChatOpenAtom,
  clearChatHistoryAction,
  hasMoreChatMessagesAtom,
  loadingChatHistoryAtom,
  loadInitialChatHistoryAction,
  loadMoreChatHistoryAction,
} from '@/atoms/modules/ai-chat';
import { selectedSchemaIdAtom } from '@/atoms/modules/sidebar';
import { api, ChatMessage } from '@/utils/api';

import * as styles from './AiChatPanel.styles';

// Helper to parse message content and extract SQL blocks
interface ParsedBlock {
  type: 'text' | 'sql';
  value: string;
}

const parseMessageContent = (content: string): ParsedBlock[] => {
  const parts: ParsedBlock[] = [];
  const regex = /```sql([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const textBefore = content.substring(lastIndex, match.index);
    if (textBefore) {
      parts.push({ type: 'text', value: textBefore });
    }
    parts.push({ type: 'sql', value: match[1] });
    lastIndex = regex.lastIndex;
  }

  const textAfter = content.substring(lastIndex);
  if (textAfter || parts.length === 0) {
    parts.push({ type: 'text', value: textAfter || content });
  }

  return parts;
};

const renderMarkdown = (text: string) => {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let inList = false;
  let listItems: React.ReactNode[] = [];

  const parseInline = (line: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*.*?\*\*|`.*?`)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(line)) !== null) {
      const matchStr = match[0];
      const matchIndex = match.index;

      if (matchIndex > lastIndex) {
        parts.push(line.substring(lastIndex, matchIndex));
      }

      if (matchStr.startsWith('**') && matchStr.endsWith('**')) {
        parts.push(
          <strong
            key={matchIndex}
            style={{ fontWeight: 'bold', color: 'inherit' }}
          >
            {matchStr.slice(2, -2)}
          </strong>
        );
      } else if (matchStr.startsWith('`') && matchStr.endsWith('`')) {
        parts.push(
          <code
            key={matchIndex}
            style={{
              backgroundColor: 'var(--gray-5)',
              padding: '2px 4px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '11px',
            }}
          >
            {matchStr.slice(1, -1)}
          </code>
        );
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex));
    }

    return parts.length > 0 ? parts : [line];
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (!inList) {
        inList = true;
        listItems = [];
      }
      listItems.push(
        <li
          key={idx}
          style={{
            marginLeft: '16px',
            marginBottom: '4px',
            listStyleType: 'disc',
          }}
        >
          {parseInline(trimmed.slice(2))}
        </li>
      );
    } else {
      if (inList) {
        elements.push(
          <ul
            key={`list-${idx}`}
            style={{ margin: '8px 0', paddingLeft: '16px' }}
          >
            {listItems}
          </ul>
        );
        inList = false;
      }

      if (trimmed.startsWith('### ')) {
        elements.push(
          <h3
            key={idx}
            style={{
              margin: '12px 0 6px 0',
              fontSize: '14px',
              fontWeight: 'bold',
              color: 'inherit',
            }}
          >
            {parseInline(trimmed.slice(4))}
          </h3>
        );
      } else if (trimmed.startsWith('## ')) {
        elements.push(
          <h2
            key={idx}
            style={{
              margin: '14px 0 8px 0',
              fontSize: '15px',
              fontWeight: 'bold',
              color: 'inherit',
            }}
          >
            {parseInline(trimmed.slice(3))}
          </h2>
        );
      } else if (trimmed.startsWith('# ')) {
        elements.push(
          <h1
            key={idx}
            style={{
              margin: '16px 0 10px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'inherit',
            }}
          >
            {parseInline(trimmed.slice(2))}
          </h1>
        );
      } else if (trimmed === '') {
        elements.push(<div key={idx} style={{ height: '8px' }} />);
      } else {
        elements.push(
          <p key={idx} style={{ margin: '4px 0', minHeight: '18px' }}>
            {parseInline(line)}
          </p>
        );
      }
    }
  });

  if (inList) {
    elements.push(
      <ul key="list-end" style={{ margin: '8px 0', paddingLeft: '16px' }}>
        {listItems}
      </ul>
    );
  }

  return elements;
};

const getCreateTableName = (statement: string): string | null => {
  const match = statement.match(
    /CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?([a-zA-Z0-9_"`\.-]+)/i
  );
  if (!match) return null;
  return match[1]
    .replace(/["'`\[\]]/g, '')
    .trim()
    .toLowerCase();
};

const getForeignKeySignature = (statement: string): string | null => {
  const match = statement.match(
    /ALTER\s+TABLE\s+(?:ONLY\s+)?([a-zA-Z0-9_"`\.-]+)\s+ADD\s+(?:CONSTRAINT\s+([a-zA-Z0-9_"`\.-]+)\s+)?FOREIGN\s+KEY\s*\(([^)]+)\)\s*REFERENCES\s+([a-zA-Z0-9_"`\.-]+)\s*\(([^)]+)\)/i
  );
  if (!match) return null;
  const clean = (s: string) => s.replace(/["'`\[\]\s]/g, '').toLowerCase();
  const table = clean(match[1]);
  const foreignKey = clean(match[3]);
  const targetTable = clean(match[4]);
  const primaryKey = clean(match[5]);
  return `${table}(${foreignKey})->${targetTable}(${primaryKey})`;
};

const simplifyDDL = (sql: string): string => {
  if (!sql || sql.trim() === '') return '';
  const statements = sql
    .split(';')
    .map(s => s.trim())
    .filter(s => s.length > 0);
  const tableMap = new Map<string, string>();
  const fkMap = new Map<string, string>();
  const indexMap = new Map<string, string>();
  const otherStatements: string[] = [];

  for (const stmt of statements) {
    const tableName = getCreateTableName(stmt);
    const fkSig = getForeignKeySignature(stmt);
    if (tableName) {
      let cleanStmt = stmt.replace(
        /,\s*ON\s+(?:VARCHAR|SET|TEXT)[^,\)]*/gi,
        ''
      );
      cleanStmt = cleanStmt.replace(
        /\bON\s+(?:VARCHAR|SET|TEXT)[^,\)]*,\s*/gi,
        ''
      );
      tableMap.set(tableName, cleanStmt);
    } else if (fkSig) {
      fkMap.set(fkSig, stmt);
    } else if (/CREATE\s+(?:UNIQUE\s+)?INDEX/i.test(stmt)) {
      const idxMatch = stmt.match(
        /CREATE\s+(?:UNIQUE\s+)?INDEX\s+(?:IF\s+NOT\s+EXISTS\s+)?([a-zA-Z0-9_"`\.-]+)\s+ON\s+([a-zA-Z0-9_"`\.-]+)/i
      );
      if (idxMatch) {
        const idxKey = `${idxMatch[2].toLowerCase()}::${idxMatch[1].toLowerCase()}`;
        indexMap.set(idxKey, stmt);
      } else {
        indexMap.set(stmt.toLowerCase(), stmt);
      }
    } else {
      otherStatements.push(stmt);
    }
  }

  return (
    [
      ...Array.from(tableMap.values()),
      ...Array.from(fkMap.values()),
      ...Array.from(indexMap.values()),
      ...otherStatements,
    ].join(';\n\n') + ';'
  );
};

const mergeDDL = (currentSql: string, newSql: string): string => {
  const currentStatements = currentSql
    .split(';')
    .map(s => s.trim())
    .filter(s => s.length > 0);
  const newStatements = newSql
    .split(';')
    .map(s => s.trim())
    .filter(s => s.length > 0);

  const mergedStatements = [...currentStatements];

  for (const newStmt of newStatements) {
    const newTableName = getCreateTableName(newStmt);
    const newFkSig = getForeignKeySignature(newStmt);

    if (newTableName) {
      const existingIdx = mergedStatements.findIndex(stmt => {
        const name = getCreateTableName(stmt);
        return name === newTableName;
      });

      if (existingIdx !== -1) {
        mergedStatements[existingIdx] = newStmt;
      } else {
        mergedStatements.push(newStmt);
      }
    } else if (newFkSig) {
      const existingIdx = mergedStatements.findIndex(stmt => {
        const sig = getForeignKeySignature(stmt);
        return sig === newFkSig;
      });

      if (existingIdx !== -1) {
        mergedStatements[existingIdx] = newStmt;
      } else {
        mergedStatements.push(newStmt);
      }
    } else {
      mergedStatements.push(newStmt);
    }
  }

  return simplifyDDL(mergedStatements.join(';\n\n') + ';');
};

export const AiChatPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useAtom(aiChatOpenAtom);
  const [activeEditor] = useAtom(activeEditorAtom);
  const schemaId = useAtomValue(selectedSchemaIdAtom);

  const messages = useAtomValue(activeChatMessagesAtom);
  const hasMore = useAtomValue(hasMoreChatMessagesAtom);
  const loadingHistory = useAtomValue(loadingChatHistoryAtom);

  const [, loadInitialChatHistory] = useAtom(loadInitialChatHistoryAction);
  const [, loadMoreChatHistory] = useAtom(loadMoreChatHistoryAction);
  const [, addChatMessages] = useAtom(addChatMessagesAction);
  const [, clearChatHistory] = useAtom(clearChatHistoryAction);

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Trigger initial chat loading when active schema changes
  useEffect(() => {
    if (schemaId) {
      loadInitialChatHistory(schemaId);
    }
  }, [schemaId, loadInitialChatHistory]);

  // Scroll to bottom when new messages arrive (non-historical ones)
  useEffect(() => {
    if (scrollRef.current && !loadingHistory) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, loading, loadingHistory]);

  // Only render panel if a schema is selected
  if (!schemaId) return null;

  const handleSend = async () => {
    if (!input.trim() || !schemaId || loading) return;

    const userMsgText = input.trim();
    setInput('');
    setLoading(true);

    const userMessage: ChatMessage = { role: 'user', content: userMsgText };
    const updatedMessages = [...messages, userMessage];

    // Optimistically update conversation history
    addChatMessages([userMessage]);

    try {
      // 1. Extract DDL of the active visual diagram
      let ddlContext = '';
      if (activeEditor) {
        try {
          ddlContext = activeEditor.getSchemaSQL();
        } catch (err) {
          console.error('Error fetching schema SQL:', err);
        }
      }

      // 2. Call backend chat API (passing schemaId to save it)
      const res = await api.sendChat(updatedMessages, ddlContext, schemaId);

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: res.reply,
      };

      // 3. Save final conversation history locally
      addChatMessages([assistantMessage]);
    } catch (err: any) {
      console.error(err);
      addChatMessages([
        {
          role: 'assistant',
          content: `❌ Error al conectar con la IA: ${err.message}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplySql = (sql: string) => {
    if (!activeEditor) {
      alert('Editor no inicializado. Asegúrate de tener un diagrama abierto.');
      return;
    }
    try {
      const currentSql = activeEditor.getSchemaSQL();
      const mergedSql = mergeDDL(currentSql, sql);
      activeEditor.setSchemaSQL(mergedSql);
    } catch (err: any) {
      alert(`Error al aplicar SQL en el diagrama: ${err.message}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div css={styles.panel(isOpen)}>
      {/* Vertical Toggle Handle */}
      <div
        css={styles.toggleHandle(isOpen)}
        onClick={() => setIsOpen(!isOpen)}
        title={isOpen ? 'Cerrar Asistente de IA' : 'Abrir Asistente de IA'}
      >
        {isOpen ? (
          <ChevronRightIcon width="16" height="16" />
        ) : (
          <ChevronLeftIcon width="16" height="16" />
        )}
      </div>

      {/* Header */}
      <div css={styles.header}>
        <Flex align="center" gap="2">
          <MagicWandIcon
            width="18"
            height="18"
            style={{ color: 'var(--accent-9)' }}
          />
          <Heading size="3">Asistente de IA (DB)</Heading>
        </Flex>
        <Flex align="center" gap="2">
          {messages.length > 0 && (
            <Button
              size="1"
              variant="ghost"
              color="red"
              onClick={() => {
                if (confirm('¿Estás seguro de que deseas vaciar el chat?')) {
                  clearChatHistory(schemaId);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              Vaciar
            </Button>
          )}
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            onClick={() => setIsOpen(false)}
          >
            <Cross1Icon width="14" height="14" />
          </IconButton>
        </Flex>
      </div>

      {/* Messages */}
      <div css={styles.scrollArea} ref={scrollRef}>
        {hasMore && (
          <Flex justify="center" style={{ margin: '8px 0 16px 0' }}>
            <Button
              size="1"
              variant="ghost"
              disabled={loadingHistory}
              onClick={() => loadMoreChatHistory(schemaId)}
              style={{ cursor: 'pointer' }}
            >
              {loadingHistory ? 'Cargando...' : 'Cargar mensajes anteriores'}
            </Button>
          </Flex>
        )}

        {messages.length === 0 && !loadingHistory && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            style={{ flexGrow: 1, padding: 20 }}
            gap="3"
          >
            <MagicWandIcon
              width="32"
              height="32"
              style={{ color: 'var(--gray-8)', opacity: 0.5 }}
            />
            <Text size="2" color="gray" align="center">
              Pregúntame cosas sobre tu base de datos, pídeme crear tablas,
              relaciones o que te explique campos específicos.
            </Text>
          </Flex>
        )}

        {messages.map((msg, index) => {
          const isUser = msg.role === 'user';
          const blocks = parseMessageContent(msg.content);

          return (
            <div
              key={index}
              css={isUser ? styles.userMessage : styles.assistantMessage}
            >
              <Text
                size="1"
                weight="medium"
                style={{
                  color: isUser ? 'var(--accent-11)' : 'var(--gray-9)',
                  marginBottom: '2px',
                }}
              >
                {isUser ? 'Tú' : 'Asistente IA'}
              </Text>

              <div css={styles.bubble(isUser)}>
                {blocks.map((block, bIdx) => {
                  if (block.type === 'text') {
                    return (
                      <span key={bIdx}>{renderMarkdown(block.value)}</span>
                    );
                  } else {
                    return (
                      <div key={bIdx} css={styles.sqlActionBlock}>
                        <div css={styles.sqlHeader}>
                          <Text
                            size="1"
                            weight="bold"
                            style={{ color: 'var(--accent-11)' }}
                          >
                            SQL GENERADO
                          </Text>
                          <Button
                            size="1"
                            variant="solid"
                            onClick={() => handleApplySql(block.value)}
                          >
                            <CheckIcon /> Aplicar al Diagrama
                          </Button>
                        </div>
                        <pre css={styles.sqlCode}>{block.value.trim()}</pre>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          );
        })}

        {loading && (
          <div css={styles.assistantMessage}>
            <Text size="1" color="gray">
              Asistente IA
            </Text>
            <div css={styles.typingIndicator}>
              <div css={styles.dot('0s')} />
              <div css={styles.dot('0.2s')} />
              <div css={styles.dot('0.4s')} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div css={styles.inputArea}>
        <div css={styles.unifiedInputWrapper}>
          <textarea
            required
            css={styles.customTextArea}
            placeholder="Pregunta o pide cambios en el DDL..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
          />
          <div css={styles.inputControls}>
            <IconButton
              size="2"
              variant="solid"
              disabled={!input.trim() || loading}
              onClick={handleSend}
              style={{ cursor: 'pointer' }}
            >
              <PaperPlaneIcon width="14" height="14" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AiChatToggle: React.FC = () => {
  return null;
};
